// File: v4/components/RoomRatesComponentV4.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetError, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

// Import hooks dan komponen anak
import useCheckAvi from '../../hooks/useCheckAviV4';
import useShowRate from '../../hooks/useShowRate';
import { RoomSelected, RoomStatus } from './CreateBookingComponentV4';
import RoomHeaderV4 from './RoomHeaderV4';
import PrimaryInfoV4 from './PrimaryInfoV4';
import GuestInfoV4 from './GuestInfoV4';
import RoomSelectionV4 from './RoomSelectionV4';
import ConfirmRatePlanChangeModal from './ConfirmRatePlanChangeModal';
import OccupancyAndTotalV4 from './OccupancyAndTotalV4';
import RatesTabFormV4 from './RateTabComponentV4';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import { Spinner } from 'react-bootstrap';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { useAuth } from '../../../../auth/context/AuthProvider';

import Select from 'react-select';

// Asumsi tipe-tipe ini sudah ada atau diimpor dari lokasi yang benar
import { StatusTypes } from '../../types/StatusTypes';
import { MarkTypes } from '../../types/MarkTypes';
import clsx from 'clsx';

const StatusBanner: React.FC<{ statusInfo: RoomStatus }> = ({ statusInfo }) => {
    const { status, message } = statusInfo;

    // Jangan tampilkan apa-apa jika status 'idle' atau 'success' (sukses tidak perlu banner permanen)
    if (status === 'idle' || status === 'success') {
        return null;
    }

    let variant = 'info';
    let icon = <Spinner animation="border" size="sm" className="me-2" />;
    let text = "Processing...";

    if (status === 'error') {
        variant = 'danger';
        icon = <IconAlertTriangle size={20} className="me-2" />;
        text = message || "An error occurred.";
    }

    return (
        <div className={`alert alert-${variant} d-flex align-items-center p-2`} role="alert">
            {icon}
            <span className="fw-bold small">{text}</span>
        </div>
    );
};

const StatusOverlay: React.FC<{ message: string }> = ({ message }) => {
    const style: React.CSSProperties = {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 10,
        borderRadius: '0.375rem',
        backgroundColor: 'rgba(209, 231, 221, 0.95)', // Warna hijau sukses
        textAlign: 'center',
        padding: '1rem',
    };

    return (
        <div style={style}>
            <IconCheck size={48} className="text-success" />
            <span className="mt-2 text-success fw-bold">{message}</span>
        </div>
    );
};

// Tipe props tidak berubah
interface RoomRatesComponentPropsV4 {
    index: number,
    register: UseFormRegister<any>,
    control: Control<any>,
    errors: FieldErrors<CreateBookingFormV4>,
    setValue: UseFormSetValue<any>
    watch: UseFormWatch<any>,
    setError: UseFormSetError<any>,
    deleteRoom: (index: number) => void
    roomSelected: RoomSelected[]
    setRoomSelected: React.Dispatch<React.SetStateAction<RoomSelected[]>>
    clearErrors: UseFormClearErrors<any>
    statusInfo: RoomStatus;
    isParentPending: boolean;
    bookingId: string | null;
    isGroupBookingComplete: boolean;
    hasDateChanged: boolean;
}

// Tipe RateRow, jika belum diekspor dari tempat lain
interface RateRow {
    date: string;
    rate: number;
}

const RoomRatesComponentV4: React.FC<RoomRatesComponentPropsV4> = ({
    index, register, control, errors, setValue, watch, setError, deleteRoom, roomSelected, setRoomSelected, clearErrors, statusInfo, isParentPending, bookingId, isGroupBookingComplete, hasDateChanged
}) => {
    const { user } = useAuth();
    const [showRates, setShowRates] = useState<boolean>(false);

    // ====================================================================
    // PERBAIKAN UTAMA: Ganti useState dengan useRef untuk snapshot yang stabil
    // ====================================================================

    // 1. Gunakan useRef untuk menyimpan nilai yang tidak akan berubah di antara re-render
    const initialRatePlanId = useRef<string | null>(null);
    const initialCustomRates = useRef<RateRow[] | null>(null);

    // 2. useEffect untuk mengambil snapshot HANYA SEKALI saat mode update
    useEffect(() => {
        // `bookingId` adalah indikator mode update.
        // `initialRatePlanId.current === null` memastikan ini hanya berjalan satu kali.
        if (bookingId && initialRatePlanId.current === null) {
            initialRatePlanId.current = watch(`bookingroom[${index}].rate_plan_id`);
            initialCustomRates.current = watch(`bookingroom[${index}].rates`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId, watch, index]); // Dependensi ini aman karena hanya untuk inisialisasi

    const handleHideAndShowRates = useCallback(() => {
        setShowRates((prev) => !prev);
    }, []);

    // ====================================================================
    // Confirm dialog rate plan (mode edit): set/ganti rate plan tidak boleh
    // langsung men-timpa `rates` secara diam-diam. Alur:
    // 1. RoomSelectionV4 (RateplansComponentV4) meng-intercept onChange lewat
    //    `handleRequestRatePlanChange` di bawah, alih-alih langsung commit.
    // 2. User pilih "Use Rate Plan Price" -> commit rate_plan_id, biarkan efek
    //    Scenario 1 di bawah (baris ~277) meng-overwrite rates seperti biasa.
    // 3. User pilih "Keep Original Price" -> commit rate_plan_id, TAPI catat rate plan
    //    tersebut di `keepOriginalForRatePlanIdRef` supaya efek Scenario-tree skip
    //    overwrite SELAMA rate_plan_id form masih sama dengan yang dicatat ini.
    //
    // CATATAN PENTING (bug yang diperbaiki): implementasi sebelumnya pakai flag
    // sekali-pakai (`skipNextAutoApplyRef = true`) yang HANYA di-reset kalau efek
    // Scenario-tree di bawah benar-benar sempat jalan lagi. Kalau efek itu TIDAK
    // jalan untuk fetch berikutnya (mis. `useShowRate` sempat serve dari cache
    // React Query sehingga `showRate`/`isRateSuccess` tidak berubah referensi),
    // flag itu nyangkut `true` SELAMANYA -- akibatnya SEMUA pilihan rate plan
    // berikutnya (termasuk "Use Rate Plan Price" yang genuine) ikut ke-skip diam-diam,
    // dan Total Accommodation terlihat "tidak refresh" walau dialog sudah dijawab.
    // Fix: ganti jadi PERBANDINGAN (bukan flag sekali-pakai) yang dievaluasi ulang
    // setiap kali efek jalan -- tidak bisa nyangkut, karena begitu rate_plan_id
    // form berubah (ke plan lain, apa pun pilihannya), perbandingan otomatis false.
    // ====================================================================
    const keepOriginalForRatePlanIdRef = useRef<string | null>(null);
    const [pendingRatePlanChange, setPendingRatePlanChange] = useState<{ id: string; name?: string } | null>(null);

    const handleRequestRatePlanChange = useCallback((newRatePlanId: string, newRatePlanName?: string) => {
        setPendingRatePlanChange({ id: newRatePlanId, name: newRatePlanName });
    }, []);

    const handleCancelRatePlanChange = useCallback(() => {
        setPendingRatePlanChange(null);
    }, []);

    const handleUseRatePlanPrice = useCallback(() => {
        if (!pendingRatePlanChange) return;
        keepOriginalForRatePlanIdRef.current = null;
        setValue(`bookingroom.${index}.rate_plan_id`, pendingRatePlanChange.id, { shouldDirty: true });
        setPendingRatePlanChange(null);
    }, [pendingRatePlanChange, setValue, index]);

    const handleKeepOriginalPrice = useCallback(() => {
        if (!pendingRatePlanChange) return;
        keepOriginalForRatePlanIdRef.current = pendingRatePlanChange.id;
        setValue(`bookingroom.${index}.rate_plan_id`, pendingRatePlanChange.id, { shouldDirty: true });
        setPendingRatePlanChange(null);
    }, [pendingRatePlanChange, setValue, index]);

    // 1. Data Fetching untuk ketersediaan
    const { data: roomAvi, isFetching, isError, error, isSuccess } = useCheckAvi({
        checkin_date: watch(`bookingroom[${index}].checkin_date`),
        checkout_date: watch(`bookingroom[${index}].checkout_date`),
        booking_id: bookingId ?? undefined,
        property_id: user?.active_property_id || undefined, // Gunakan properti aktif pengguna jika ada
    });

    // 2. Data Fetching untuk tarif harian
    const { data: showRate, isFetching: isRateFetching, isSuccess: isRateSuccess, isError: isRateError, error: rateError } = useShowRate({
        checkin_date: watch(`bookingroom[${index}].checkin_date`),
        checkout_date: watch(`bookingroom[${index}].checkout_date`),
        rate_plan_id: watch(`bookingroom[${index}].rate_plan_id`),
    });

    const handleUpdateRatesFromTotal = useCallback((newTotal: number) => {
        const currentRates = watch(`bookingroom.${index}.rates`);

        if (!currentRates || currentRates.length === 0) {
            toast.error("Cannot distribute total because there are no daily rates available.");
            return;
        }

        const numberOfNights = currentRates.length;
        const baseDailyRate = Math.floor((newTotal / numberOfNights) * 100) / 100;
        const totalFromBaseRates = baseDailyRate * numberOfNights;
        let remainder = parseFloat((newTotal - totalFromBaseRates).toFixed(2));

        // PERBAIKAN: Tambahkan tipe `: number` ke `rateIndex`
        const updatedRates = currentRates.map((rate: RateRow, rateIndex: number) => {
            let finalRateForThisNight = baseDailyRate;

            if (rateIndex === 0) {
                finalRateForThisNight += remainder;
            }

            return {
                ...rate,
                rate: parseFloat(finalRateForThisNight.toFixed(2)),
            };
        });

        setValue(`bookingroom.${index}.rates`, updatedRates, {
            shouldDirty: true,
            shouldValidate: true
        });

        toast.success(`Total accommodation updated and distributed across ${numberOfNights} nights.`);

    }, [index, watch, setValue]);

    // Efek untuk menangani error dari `useCheckAvi`
    useEffect(() => {
        if (isError) {
            toast.error((error as any)?.response?.data?.message || 'Failed to check availability.');
        }
    }, [isError, error]);

    // Efek untuk menangani error dari `useShowRate`
    useEffect(() => {
        if (isRateError) {
            const errorString = JSON.stringify((rateError as any)?.response?.data?.errors);
            console.log('Rate Error:', errorString);
            // toast.error((rateError as any)?.response?.data?.message || 'Failed to fetch rates.');
            toast.error(`Failed to fetch rates: ${errorString}`, {
                duration: 10000, // Tampilkan selama 5 detik
                // Menampilkan atau menyembunyikan tombol tutup (biasanya sudah aktif secara default)
                closeButton: true
            });
        }
    }, [isRateError, rateError]);

    // Efek untuk mengisi tarif harian ke dalam form state
    // useEffect(() => {
    //     if (isRateSuccess && showRate?.data?.rates) {
    //         setValue(`bookingroom[${index}].rates`, showRate.data.rates);
    //     }
    // }, [isRateSuccess, showRate, setValue, index]);

    // 3. useEffect utama sekarang membaca dari .current, memastikan nilainya selalu yang asli

    // ============ OLD Code ================
    // useEffect(() => {
    //     if (isRateSuccess && showRate?.data?.rates) {
    //         const currentRatePlanId = watch(`bookingroom[${index}].rate_plan_id`);
    //         const isUpdateMode = !!initialRatePlanId.current;

    //         if (!isUpdateMode) {
    //             // Skenario CREATE: Selalu gunakan tarif dari API
    //             setValue(`bookingroom[${index}].rates`, showRate.data.rates);
    //         } else {
    //             // Skenario UPDATE:
    //             if (currentRatePlanId === initialRatePlanId.current) {
    //                 // Pengguna beralih KEMBALI ke rate plan asli. Pulihkan tarif kustom.
    //                 if (initialCustomRates.current) {
    //                     setValue(`bookingroom[${index}].rates`, initialCustomRates.current, { shouldDirty: true });
    //                 }
    //             } else {
    //                 // Pengguna beralih ke rate plan BARU. Gunakan tarif standar dari API.
    //                 setValue(`bookingroom[${index}].rates`, showRate.data.rates, { shouldDirty: true });
    //             }
    //         }
    //     }
    //     // Refs tidak perlu ada di dependency array
    // }, [isRateSuccess, showRate, setValue, index, watch]);

    // useEffect(() => {
    //     if (isRateSuccess && showRate?.data?.rates) {
    //         const currentRatePlanId = watch(`bookingroom[${index}].rate_plan_id`);

    //         // **SKENARIO 1: TANGGAL TELAH DIUBAH**
    //         // Jika tanggal diubah, kita HARUS menimpa tarif dengan data baru dari API.
    //         // Ini adalah prioritas tertinggi dan memperbaiki bug awal.
    //         if (hasDateChanged) {
    //             setValue(`bookingroom[${index}].rates`, showRate.data.rates, { shouldDirty: true });
    //             return; // Hentikan proses lebih lanjut
    //         }

    //         // **SKENARIO 2: PENGGUNA PINDAH KE PAKET TARIF BARU**
    //         // Jika pengguna memilih paket tarif yang berbeda dari yang asli, gunakan data API.
    //         if (currentRatePlanId !== initialRatePlanId.current) {
    //             setValue(`bookingroom[${index}].rates`, showRate.data.rates, { shouldDirty: true });
    //         }
    //         // **SKENARIO 3: PENGGUNA KEMBALI KE PAKET TARIF AWAL**
    //         // Jika pengguna kembali ke paket tarif asli DAN tanggal tidak berubah,
    //         // pulihkan tarif kustom yang sudah disimpan.
    //         else {
    //             if (initialCustomRates.current) {
    //                 setValue(`bookingroom[${index}].rates`, initialCustomRates.current, { shouldDirty: true });
    //             } else {
    //                 // Fallback jika snapshot kosong, gunakan data API
    //                 setValue(`bookingroom[${index}].rates`, showRate.data.rates, { shouldDirty: true });
    //             }
    //         }
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [isRateSuccess, showRate, setValue, index, watch, hasDateChanged]);

    // Di dalam file: RoomRatesComponentV4.tsx

    // Di dalam file: RoomRatesComponentV4.tsx

    // Pastikan untuk menerima prop baru
    // interface RoomRatesComponentPropsV4 { ..., hasDateChanged: boolean; }

    // Buang tarif yang jatuh di luar [checkin, checkout) setiap kali tanggal berubah.
    // Dipisah dari efek pengganti-tarif di bawah karena efek itu bisa return lebih awal
    // ("Keep Original Price") atau menunggu fetch useShowRate; tanpa ini, memundurkan
    // checkout meninggalkan tarif tanggal >= checkout di form dan submit ditolak backend
    // ("The rates.N.date field must be a date before checkout date").
    const watchedCheckin = watch(`bookingroom.${index}.checkin_date`);
    const watchedCheckout = watch(`bookingroom.${index}.checkout_date`);
    useEffect(() => {
        if (!watchedCheckin || !watchedCheckout) return;
        const currentRates: RateRow[] | undefined = watch(`bookingroom.${index}.rates`);
        if (!Array.isArray(currentRates) || currentRates.length === 0) return;

        const checkin = String(watchedCheckin).slice(0, 10);
        const checkout = String(watchedCheckout).slice(0, 10);
        const inRange = currentRates.filter(r => {
            const d = String(r.date).slice(0, 10);
            return d >= checkin && d < checkout;
        });

        if (inRange.length !== currentRates.length) {
            setValue(`bookingroom.${index}.rates`, inRange, { shouldDirty: true, shouldValidate: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedCheckin, watchedCheckout, index]);

    useEffect(() => {
        console.log('1. Data dari API (useShowRate):', showRate?.data);

        const currentRatePlanId = watch(`bookingroom[${index}].rate_plan_id`);

        // User memilih "Keep Original Price" untuk rate plan yang SAAT INI aktif di
        // form -> jangan timpa rates. Dievaluasi ulang setiap kali efek ini jalan
        // (bukan flag sekali-pakai) supaya tidak bisa nyangkut kalau fetch berikutnya
        // kebetulan tidak memicu efek ini lagi (lihat catatan di deklarasi ref di atas).
        if (keepOriginalForRatePlanIdRef.current && keepOriginalForRatePlanIdRef.current === currentRatePlanId) {
            return;
        }

        if (isRateSuccess && showRate?.data?.rates) {

            const newRatesFromApi = showRate.data.rates;

            // --- AWAL DARI POHON KEPUTUSAN FINAL ---

            // SCENARIO 1: Pengguna pindah ke Rate Plan BARU (misal: dari A ke B).
            // Ini adalah prioritas tertinggi.
            if (currentRatePlanId !== initialRatePlanId.current) {
                console.log('2. AKAN mengatur rates (Scenario 1 - Rate Plan Baru):', newRatesFromApi);
                // AKSI: Ganti total semua tarif dengan data standar dari API.
                // PENTING: pakai notasi dot (bukan bracket) -- `ratesFromForm` (useWatch di
                // bawah, sumber currentAccommodationTotal) di-subscribe dengan notasi dot.
                // setValue dgn notasi bracket TIDAK memicu re-render useWatch itu (meski
                // NILAI form tetap benar & tersimpan saat submit) -- akibatnya field "Total
                // Accommodation" terlihat nyangkut/tidak refresh sampai user klik tombol
                // Refresh manual (yang kebetulan pakai notasi dot).
                setValue(`bookingroom.${index}.rates`, newRatesFromApi, { shouldDirty: true });

                // SCENARIO 2: Rate Plan tetap sama, TAPI pengguna mengubah tanggal.
            } else if (hasDateChanged) {
                // AKSI: Lakukan "intelligent merge" untuk mempertahankan editan manual
                // pada tanggal yang tumpang tindih.
                const currentRatesInForm = watch(`bookingroom.${index}.rates`) || [];
                const currentRatesMap = new Map(currentRatesInForm.map((rate: RateRow) => [rate.date, rate.rate]));
                const mergedRates = newRatesFromApi.map(apiRate => {
                    return {
                        date: apiRate.date,
                        rate: currentRatesMap.has(apiRate.date)
                            ? currentRatesMap.get(apiRate.date)
                            : apiRate.rate
                    };
                });

                console.log('2. AKAN mengatur rates (Scenario 2 - Tanggal Berubah):', mergedRates);

                setValue(`bookingroom.${index}.rates`, mergedRates, { shouldDirty: true });

                // SCENARIO 3: Pengguna kembali ke Rate Plan AWAL (A -> B -> A) dan tanggal tidak berubah.
            } else {
                // AKSI: Pulihkan tarif asli yang sudah disimpan saat komponen
                // pertama kali dimuat, LENGKAP dengan editan manualnya.
                if (initialCustomRates.current) {
                    // Pulihkan editan asli HANYA untuk tanggal yang masih ada di rentang
                    // sekarang (newRatesFromApi = tepat [checkin, checkout)). `hasDateChanged`
                    // hanya memantau kamar pertama, jadi pada reservasi grup mengubah tanggal
                    // kamar lain jatuh ke sini; memulihkan snapshot mentah akan membawa kembali
                    // tanggal >= checkout baru dan submit ditolak backend.
                    const initialMap = new Map(
                        initialCustomRates.current.map((r: RateRow) => [String(r.date).slice(0, 10), r.rate])
                    );
                    const restoredRates = newRatesFromApi.map(apiRate => {
                        const key = String(apiRate.date).slice(0, 10);
                        return { date: apiRate.date, rate: initialMap.has(key) ? initialMap.get(key) : apiRate.rate };
                    });
                    console.log('2. AKAN mengatur rates (Scenario 3 - Kembali ke Asli):', restoredRates);
                    setValue(`bookingroom.${index}.rates`, restoredRates, { shouldDirty: true });
                } else {
                    console.log('2. AKAN mengatur rates (Scenario 3 - Fallback):', newRatesFromApi);
                    setValue(`bookingroom.${index}.rates`, newRatesFromApi, { shouldDirty: true }); // Fallback
                }
            }
            // --- AKHIR DARI POHON KEPUTUSAN FINAL ---
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRateSuccess, showRate, setValue, index, hasDateChanged]); // Tambahkan hasDateChanged

    // ====================================================================
    // PERBAIKAN UTAMA: Logika `useMemo` yang disempurnakan
    // ====================================================================
    // const ratesFromForm = watch(`bookingroom.${index}.rates`);
    const ratesFromForm = useWatch({
        control,
        name: `bookingroom.${index}.rates`
    });
    // const totalAccommodation = useMemo(() => {
    //     const currentRatePlanId = watch(`bookingroom.${index}.rate_plan_id`);
    //     const isUpdateMode = !!initialRatePlanId.current;

    //     // Skenario 1: Pengguna telah beralih ke rate plan BARU di mode update.
    //     // Gunakan total dari API sebagai sumber kebenaran.
    //     if (isUpdateMode && currentRatePlanId !== initialRatePlanId.current && isRateSuccess && showRate?.data?.total_rate) {
    //         return showRate.data.total_rate;
    //     }

    //     // Skenario 2: Semua kasus lainnya (Create, load awal Update, kembali ke rate plan asli).
    //     // Sumber kebenaran adalah apa yang ada di state form saat ini.
    //     return ratesFromForm?.reduce(
    //         (sum: number, rate: RateRow) => sum + (Number(rate.rate) || 0),
    //         0
    //     ) ?? 0;

    // }, [ratesFromForm, isRateSuccess, showRate, watch, index]);

    console.log(`3. Nilai rates DARI form untuk dihitung (kamar index ${index}):`, ratesFromForm);

    const currentAccommodationTotal = useMemo(() => {
        // PERBAIKAN UTAMA:
        // Prioritaskan perhitungan dari form JIKA form sudah berisi data rate.
        // Ini memastikan editan manual oleh pengguna akan selalu tercermin.
        if (ratesFromForm && ratesFromForm.length > 0) {
            return ratesFromForm.reduce((sum: number, rate: RateRow) => sum + (Number(rate.rate) || 0), 0) ?? 0;
        }

        // Jika form masih kosong TAPI API sudah berhasil,
        // gunakan total langsung dari API untuk menghindari masalah timing.
        if (isRateSuccess && showRate?.data?.total_rate) {
            return showRate.data.total_rate;
        }

        // Fallback jika belum ada data sama sekali.
        return 0;

    }, [ratesFromForm, isRateSuccess, showRate]); // Tambahkan isRateSuccess dan showRate sebagai dependensi

    console.log('4. Hasil AKHIR kalkulasi (currentAccommodationTotal):', currentAccommodationTotal);

    const revertPricePoint = useMemo(() => {
        const isUpdateMode = !!initialRatePlanId.current;
        const currentRatePlanId = watch(`bookingroom[${index}].rate_plan_id`);

        if (isUpdateMode) {
            // KASUS 1: MODE UPDATE, DAN PENGGUNA MASIH DI RATE PLAN ASLI
            if (currentRatePlanId === initialRatePlanId.current) {
                // Titik acuan adalah total dari tarif kustom yang disimpan saat load.
                if (initialCustomRates.current) {
                    return initialCustomRates.current.reduce((sum, rate) => sum + (Number(rate.rate) || 0), 0);
                }
            }
            // KASUS 2: MODE UPDATE, DAN PENGGUNA TELAH BERPINDAH KE RATE PLAN BARU
            // KASUS 3: MODE CREATE
            // Untuk kedua kasus ini, titik acuan adalah harga standar dari rate plan yang sedang aktif.
            if (isRateSuccess && showRate?.data?.total_rate) {
                return showRate.data.total_rate;
            }
        } else {
            // Ini juga menangani Mode Create
            if (isRateSuccess && showRate?.data?.total_rate) {
                return showRate.data.total_rate;
            }
        }

        // Fallback jika tidak ada data sama sekali
        return currentAccommodationTotal;

    }, [initialRatePlanId, initialCustomRates, isRateSuccess, showRate, watch, index, currentAccommodationTotal]);

    return (
        <>
        {/* <div className="position-relative mb-4 p-3 border rounded-3"> */}
        <div className="position-relative mb-4 p-3 border rounded-3 bg-white">

            {isGroupBookingComplete && <StatusOverlay message="Booked Successfully" />}

            <StatusBanner statusInfo={statusInfo} />
            <div style={{ opacity: isParentPending && status === 'idle' ? 0.5 : 1, pointerEvents: isParentPending ? 'none' : 'auto' }}>
                {/* <RoomHeaderV4 index={index} deleteRoom={deleteRoom} /> */}
                <fieldset disabled={isParentPending}>
                    <div className='mb-2'>
                        <RoomHeaderV4 index={index} deleteRoom={deleteRoom} isUpdateMode={!!bookingId} />
                    </div>

                    {/* 3. Buat container untuk dropdown di sisi kanan */}
                    <div className="d-flex gap-2" style={{ minWidth: '350px' }}>
                        {/* Status Dropdown */}
                        <div className="flex-grow-1">
                            <label className="form-label small mb-1" htmlFor={`bookingroom[${index}].status`}>Status</label>
                            <Controller
                                control={control}
                                name={`bookingroom.${index}.status`}
                                render={({ field }) => (
                                    <Select
                                        classNamePrefix="react-select"
                                        className={clsx('react-select-container', { 'is-invalid': errors.bookingroom?.[index]?.status })}
                                        options={StatusTypes}
                                        value={StatusTypes.find((status) => status.value === field.value)}
                                        onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                    />
                                )}
                            />
                            {errors.bookingroom?.[index]?.status && <div className="invalid-feedback d-block">{errors.bookingroom[index]?.status?.message}</div>}
                        </div>

                        {/* Mark Dropdown */}
                        <div className="flex-grow-1">
                            <label className="form-label small mb-1" htmlFor={`bookingroom[${index}].mark`}>Mark as</label>
                            <Controller
                                control={control}
                                name={`bookingroom.${index}.mark`}
                                render={({ field }) => (
                                    <Select
                                        classNamePrefix="react-select"
                                        className={clsx('react-select-container', { 'is-invalid': errors.bookingroom?.[index]?.mark })}
                                        options={MarkTypes}
                                        value={MarkTypes.find((mark) => mark.value === field.value)}
                                        onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                        isClearable
                                    />
                                )}
                            />
                            {errors.bookingroom?.[index]?.mark && <div className="invalid-feedback d-block">{errors.bookingroom[index]?.mark?.message}</div>}
                        </div>
                    </div>

                    <div className="row mb-3 align-items-start">
                        <PrimaryInfoV4 index={index} control={control} register={register} errors={errors} isUpdateMode={!!bookingId} />
                        <GuestInfoV4
                            index={index}
                            register={register}
                            errors={errors}
                            watch={watch}
                            setValue={setValue}
                        />

                        <div className="col-sm-4 ps-4">
                            {isFetching ? (
                                <div className='mt-4 d-flex flex-column align-items-center'>
                                    <FormSpinner />
                                    <div className="text-center mt-4">Checking Room Availability...</div>
                                </div>
                            ) : (isSuccess && roomAvi) ? (
                                <RoomSelectionV4
                                    index={index}
                                    control={control}
                                    watch={watch}
                                    setValue={setValue}
                                    errors={errors}
                                    roomAvi={roomAvi}
                                    roomSelected={roomSelected}
                                    setRoomSelected={setRoomSelected}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    isUpdateMode={!!bookingId}
                                    onRequestRatePlanChange={handleRequestRatePlanChange}
                                />
                            ) : null}
                        </div>
                    </div>

                    {isSuccess && watch(`bookingroom[${index}].room_type_id`) && (
                        <OccupancyAndTotalV4
                            index={index}
                            control={control}
                            errors={errors}
                            isRateSuccess={isRateSuccess}
                            isRateFetching={isRateFetching}
                            revertPricePoint={revertPricePoint}
                            onUpdateTotal={handleUpdateRatesFromTotal}
                            // BARU: Teruskan total yang sudah dihitung sebagai prop
                            currentAccommodationTotal={currentAccommodationTotal}
                        />
                    )}

                    {isRateSuccess && !isRateFetching && (
                        <div className="row mb-3 align-items-start">
                            <div className="col-sm-4">
                                <button type="button" className="btn btn-primary" onClick={handleHideAndShowRates}>
                                    {showRates ? 'Hide Rates' : 'Show Rates'}
                                </button>
                            </div>
                        </div>
                    )}

                    {isRateSuccess && !isRateFetching && showRates && watch(`bookingroom[${index}].rates`) && (
                        <RatesTabFormV4 index={index} control={control} watch={watch} setValue={setValue} errors={errors} />
                    )}
                </fieldset>
            </div>
        </div>
        <ConfirmRatePlanChangeModal
            show={!!pendingRatePlanChange}
            onClose={handleCancelRatePlanChange}
            onUseRatePlanPrice={handleUseRatePlanPrice}
            onKeepOriginalPrice={handleKeepOriginalPrice}
            newRatePlanName={pendingRatePlanChange?.name}
        />
        </>
    );
}

export default RoomRatesComponentV4;